import jsPDF from 'jspdf';
// Helper function to convert image to base64
const loadImageAsBase64 = (imageSrc) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const base64String = canvas.toDataURL('image/jpeg');
      resolve(base64String);
    };
    img.onerror = reject;
    img.src = imageSrc;
  });
};

export const generateAnalysisPDF = async (
  analysisData,
  deepAnalysisCache,
  fileName,
  questions,
  additionalQuestions = [],
  reportType = 'Investment'
) => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.width;
  const pageHeight = pdf.internal.pageSize.height;
  const margin = 20;
  const maxWidth = pageWidth - 2 * margin;
  let yPosition = margin;


  // Helper function to add watermark to current page
  const addWatermark = () => {
    // Set watermark properties
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(50);
    pdf.setTextColor(220, 220, 220); // Much lighter gray for transparency effect

    // Position watermark in the center of the page
    const centerX = pageWidth / 2;
    const centerY = pageHeight / 2;

    const watermarkText = "CONFIDENTIAL";

    // Add single centered watermark with rotation
    try {
      pdf.text(watermarkText, centerX, centerY, {
        angle: -45,
        align: 'center',
        baseline: 'middle'
      });
    } catch (error) {
      // Fallback: simple centered text without rotation
      console.warn('Watermark rotation failed, using simple text:', error);
      pdf.text(watermarkText, centerX, centerY, { align: 'center' });
    }
  };

  // Add logo to first page
  // yPosition = addLogo(yPosition);


  // Helper function to add text with word wrapping
  const addWrappedText = (text, x, y, maxWidth, fontSize = 12) => {
    pdf.setFontSize(fontSize);
    const lines = pdf.splitTextToSize(text, maxWidth);

    // Check if we need a new page
    if (y + (lines.length * (fontSize * 0.4)) > pageHeight - margin) {
      pdf.addPage();
      y = margin;
    }

    pdf.text(lines, x, y);
    return y + (lines.length * (fontSize * 0.4)) + 5;
  };

  // Helper function to check if we need a new page
  const checkNewPage = (requiredSpace) => {
    if (yPosition + requiredSpace > pageHeight - margin) {
      pdf.addPage();
      yPosition = margin;
    }
  };

  // Title
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(20);
  pdf.text(`${reportType} Contract Analysis Report`, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 25;

  // Contract filename
  if (fileName) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);
    yPosition = addWrappedText(`Contract File: ${fileName}`, margin, yPosition, maxWidth);
    yPosition += 5;
  }

  // Generation date
  const date = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  yPosition = addWrappedText(`Generated on: ${date}`, margin, yPosition, maxWidth);
  yPosition += 15;

  // Main Analysis Section
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  checkNewPage(20);
  pdf.text("Contract Analysis", margin, yPosition);
  yPosition += 15;

  // Process each question and answer
  const questionKeys = Object.keys(analysisData)
    .filter(key => key.startsWith('Question'))
    .sort((a, b) => {
      const numA = parseInt(a.replace('Question', ''), 10);
      const numB = parseInt(b.replace('Question', ''), 10);
      return numA - numB;
    });

  questionKeys.forEach((key, index) => {
    const question = questions[index];
    const [answer, section] = analysisData[key] || ["No answer provided.", "N/A"];

    checkNewPage(40); // Ensure we have space for question header

    // Question
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    yPosition = addWrappedText(`${index + 1}. ${question}`, margin, yPosition, maxWidth, 13);

    // Answer
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    yPosition = addWrappedText(`Answer: ${answer}`, margin + 10, yPosition, maxWidth - 10, 11);

    // Section reference
    if (section && section !== "N/A") {
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(10);
      yPosition = addWrappedText(`Reference: ${section}`, margin + 10, yPosition, maxWidth - 10, 10);
    }

    yPosition += 10; // Space between questions
  });

  // Add additional questions section if available
  if (additionalQuestions && additionalQuestions.length > 0) {
    // Check if we need a new page for additional questions
    checkNewPage(40);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.text(`Additional Questions (${additionalQuestions.length})`, margin, yPosition);
    yPosition += 15;

    additionalQuestions.forEach((item, index) => {
      checkNewPage(40); // Ensure we have space for question header

      const questionNumber = questionKeys.length + index + 1;

      // Additional Question
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(13);
      yPosition = addWrappedText(`${questionNumber}. ${item.question}`, margin, yPosition, maxWidth, 13);

      // Additional Answer
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      yPosition = addWrappedText(`Answer: ${item.answer}`, margin + 10, yPosition, maxWidth - 10, 11);

      // Section reference for additional questions
      if (item.section_reference && item.section_reference !== "N/A" && item.section_reference !== "Not specified in available sections") {
        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(10);
        yPosition = addWrappedText(`Reference: ${item.section_reference}`, margin + 10, yPosition, maxWidth - 10, 10);
      }

      yPosition += 10; // Space between additional questions
    });

    yPosition += 10; // Extra space after additional questions section
  }

  // Add deeper analysis if available
  const hasDeepAnalysis = Object.keys(deepAnalysisCache).length > 0;
  if (hasDeepAnalysis) {
    // Add new page for deeper analysis
    pdf.addPage();
    yPosition = margin;


    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.text("Deeper Analysis Questions", margin, yPosition);
    yPosition += 15;

    Object.entries(deepAnalysisCache).forEach(([originalQuestion, deepAnalysis]) => {
      checkNewPage(30);

      // Original question header
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(12);
      yPosition = addWrappedText(`Original Question: ${originalQuestion}`, margin, yPosition, maxWidth, 12);
      yPosition += 5;

      if (deepAnalysis && deepAnalysis.generated_questions && deepAnalysis.generated_questions.length > 0) {
        deepAnalysis.generated_questions.forEach((item, index) => {
          checkNewPage(25);

          // Generated question
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(11);
          yPosition = addWrappedText(`Q${index + 1}: ${item.question}`, margin + 10, yPosition, maxWidth - 20, 11);

          // Generated answer
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(10);
          yPosition = addWrappedText(`A${index + 1}: ${item.answer}`, margin + 15, yPosition, maxWidth - 25, 10);

          // Section reference for deeper analysis
          if (item.section_reference) {
            pdf.setFont("helvetica", "italic");
            pdf.setFontSize(9);
            yPosition = addWrappedText(`Reference: ${item.section_reference}`, margin + 15, yPosition, maxWidth - 25, 9);
          }

          yPosition += 8;
        });
      } else {
        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(10);
        yPosition = addWrappedText("No deeper analysis questions generated for this item.", margin + 10, yPosition, maxWidth - 20, 10);
      }

      yPosition += 15; // Space between original questions
    });
  }

  // Add watermark and footer to all pages
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);

    // Add watermark to each page
    addWatermark();

    // Add footer
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(0, 0, 0); // Reset to black for footer text
    pdf.text(
      `${reportType} Contract Analysis Report - Page ${i} of ${pageCount}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
  }

  return pdf;
};
